/**
 * Streaming Configuration Service
 * 
 * Fetches and caches streaming API configuration from the backend.
 * This keeps API keys secure by not exposing them in the frontend bundle.
 */

import { StreamingConfig } from '../../types/streaming';
import api from '../api';

class StreamingConfigService {
  private config: StreamingConfig | null = null;
  private fetchPromise: Promise<StreamingConfig> | null = null;

  /**
   * Get streaming configuration from backend
   * Caches the result for subsequent calls
   */
  async getConfig(): Promise<StreamingConfig> {
    // Return cached config if available
    if (this.config) {
      return this.config;
    }

    // If already fetching, wait for that request
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Fetch from backend
    this.fetchPromise = this.fetchConfig();
    
    try {
      this.config = await this.fetchPromise;
      return this.config;
    } finally {
      this.fetchPromise = null;
    }
  }

  /**
   * Fetch configuration from backend API
   */
  private async fetchConfig(): Promise<StreamingConfig> {
    try {
      const response = await api.get('/stream/config');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch streaming config:', error);
      
      // Return default disabled config on error
      return {
        eulerStream: {
          wsUrl: '',
          apiKey: '',
          enabled: false
        },
        youtube: {
          apiKey: '',
          enabled: false
        },
        twitch: {
          enabled: false
        }
      };
    }
  }

  /**
   * Clear cached config (useful for logout or refresh)
   */
  clearCache(): void {
    this.config = null;
    this.fetchPromise = null;
  }

  /**
   * Check if TikTok streaming is enabled
   */
  async isTikTokEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.eulerStream.enabled && !!config.eulerStream.apiKey;
  }

  /**
   * Check if YouTube streaming is enabled
   */
  async isYouTubeEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.youtube.enabled && !!config.youtube.apiKey;
  }

  /**
   * Check if Twitch streaming is enabled
   */
  async isTwitchEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.twitch.enabled;
  }
}

export const streamingConfigService = new StreamingConfigService();

